'use client';

import React, { useMemo, useState } from 'react';
import Link from 'next/link';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Button, Card, CardContent, Divider, SxProps, Theme, Typography } from '@mui/material';

import { EventMarket, EventResponse } from '@/types';
import { resolveOutcomeData } from '@/utils';
import { SportsFuturesRow } from './SportsFuturesRow';

import { ROUTES } from '@/constants';

interface SportsFuturesCardProps {
  event: EventResponse;
  initialLimit?: number;
  rowItemNameSx?: SxProps<Theme>;
  rowPercentSx?: SxProps<Theme>;
  titleSx?: SxProps<Theme>;
  showLinearProgress?: boolean;
  allowExpand?: boolean;
  selectedFutureTagId?: number | null;
}

export const SportsFuturesCard: React.FC<SportsFuturesCardProps> = ({
  event,
  initialLimit = 3,
  rowItemNameSx,
  rowPercentSx,
  titleSx,
  showLinearProgress = true,
  allowExpand = false,
  selectedFutureTagId,
}) => {
  const [showAll, setShowAll] = useState(false);

  const markets = Array.isArray(event?.markets) ? event.markets : [];

  const sortedMarkets = useMemo(() => {
    return [...markets]
      .filter((market) => {
        return market.active == true;
      })
      .sort((a, b) => {
        const { price: priceA } = resolveOutcomeData(a);
        const { price: priceB } = resolveOutcomeData(b);
        return priceB - priceA;
      });
  }, [markets]);

  const effectiveLimit = allowExpand ? initialLimit : 5;

  const displayedMarkets = useMemo(() => {
    if (allowExpand) {
      return showAll ? sortedMarkets : sortedMarkets.slice(0, effectiveLimit);
    }
    return sortedMarkets.slice(0, 5);
  }, [allowExpand, showAll, sortedMarkets, effectiveLimit]);

  const displayTitle = useMemo(() => {
    let title = event?.title || 'Unknown Event';
    if (selectedFutureTagId === 306 && title) {
      // Split by either en-dash '–' or standard hyphen '-'
      if (title.includes('–')) {
        const parts = title.split('–');
        title = parts[parts.length - 1].trim();
      } else if (title.includes('-')) {
        const parts = title.split('-');
        title = parts[parts.length - 1].trim();
      }
    }
    return title;
  }, [event?.title, selectedFutureTagId]);

  if (!event || markets.length === 0) return null;

  const hasMore = sortedMarkets.length > effectiveLimit;

  const renderMarketRow = (market: EventMarket, index: number) => (
    <SportsFuturesRow
      key={market.id ?? index}
      market={market}
      isLeading={index === 0}
      showLinearProgress={showLinearProgress}
      eventSlug={event.slug}
      itemNameSx={rowItemNameSx}
      percentSx={rowPercentSx}
    />
  );

  const renderedMarketRows = displayedMarkets.map(renderMarketRow);

  const toggleShowAll = () => setShowAll(!showAll);

  return (
    <Card
      sx={{
        bgcolor: 'background.paper',
        backgroundImage: 'none',
        borderRadius: '1rem',
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Link
        href={ROUTES.MARKET_DETAILS(event?.slug)}
        style={{
          textDecoration: 'none',
          display: 'block',
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        <Box
          sx={{
            padding: '1.25rem 1.5rem',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              fontSize: '1rem',
              lineHeight: 1.3,
              letterSpacing: '-0.01em',
              color: 'text.primary',
              ...titleSx,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {displayTitle}
          </Typography>
        </Box>
      </Link>

      {/* Card Content - Market Rows */}
      <CardContent
        sx={{
          padding: '0.25rem 1.5rem',
          flexGrow: 1,
          '&:last-child': {
            paddingBottom: '0.25rem',
          },
        }}
      >
        {renderedMarketRows}
      </CardContent>

      {/* Expand/Collapse Footer */}
      {allowExpand && hasMore && (
        <>
          <Divider sx={{ mx: '0.5rem' }} />
          <Button
            onClick={toggleShowAll}
            endIcon={showAll ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            sx={{
              width: '100%',
              textTransform: 'none',
              color: 'text.secondary',
              padding: '1rem 0',
              borderRadius: '0 0 1.5rem 1.5rem',
              fontWeight: 700,
              fontSize: '0.875rem',
              '&:hover': {
                bgcolor: 'action.hover',
                color: 'text.primary',
              },
            }}
          >
            {showAll ? 'Show Less' : `Show ${sortedMarkets.length - initialLimit} More`}
          </Button>
        </>
      )}
    </Card>
  );
};
